import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { RangeFeature } from "src/app/model/catalog/catalog.model";
import { LensSide } from "src/app/model/configuration/configuration";
import { GlobalStorageService } from "src/app/service-utils/global-storage.service";
import { i18nService } from "src/app/service-utils/i18n.service";
import { ValidatorService } from "src/app/service-utils/validator-service";
import { NewOrderStoreService } from "../../new-order-store-service.service";

@Component({
    selector: 'app-spectacular-refraction',
    templateUrl: './spectacular-refraction.component.html',
    styleUrls: ['./spectacular-refraction.component.scss']
})
export class SpectacularRefractionComponent implements OnInit {

    @Input() side: LensSide;
    @Output() copyEmit = new EventEmitter<LensSide>();
    @Output() switchEmit = new EventEmitter<LensSide>();

    faIcon = faInfo;
    
    form: FormGroup;
    LensSide = LensSide;

    axisDisable: boolean = true;
    hsaDisable: boolean = true;
    axisCurrentValue: number = 0;
    hsaCurrentValue: number;

    constructor(
        public i18nService: i18nService,
        private formBuilder: FormBuilder,
        private newOrderStoreService: NewOrderStoreService,
        private router: Router,
        private validatorService: ValidatorService,
        private globalStorageService: GlobalStorageService
    ) {}

    ngOnInit(): void {
        this.initForm();
    }

    public initForm() {
        this.hsaCurrentValue = this.globalStorageService.hsa;
        this.form = this.formBuilder.group({
            sphere: ["", [this.validatorService.numericValidator('Sphere2'), this.validatorService.requiredFieldValidator('Sphere2'), this.validatorService.rangeValidator(new RangeFeature('Sphere2', true, -50, 50, 0.25))]],
            cylinder: [0, [this.validatorService.numericValidator('Cylinder'), this.validatorService.requiredFieldValidator('Cylinder'), this.validatorService.rangeValidator(new RangeFeature('Cylinder', true, -20, 20, 0.25))]],
            axis: [{value: '', disabled: true}, [this.validatorService.numericValidator('Axis'), this.validatorService.requiredFieldValidator('Axis'), this.validatorService.rangeValidator(new RangeFeature('Axis', true, 0, 179, 1))]],
            hsa: [{value: '', disabled: true}, [this.validatorService.numericValidator('HSA2'), this.validatorService.requiredFieldValidator('HSA2'), this.validatorService.rangeValidator(new RangeFeature('HSA2', true, 5, 35, 1))]],
            addition: [0, [this.validatorService.numericValidator('Addition'), this.validatorService.requiredFieldValidator('Addition'), this.validatorService.rangeValidator(new RangeFeature('Addition', true, 0, 4, 0))]],
            sphereCalc: [{value: '-', disabled: true},],
            cylinderCalc: [{value: '-', disabled: true},],
            axisCalc: [{value: '-', disabled: true},],
            hsaCalc: [{value: '-', disabled: true},],
        });
        this.initHandlerForm();
    }

    private initHandlerForm() {
        this.form.get('sphere').valueChanges.subscribe(() =>{
            this.recalculateSpectacularRefraction();
        });
    
        this.form.get('cylinder').valueChanges.subscribe(() =>{
            this.recalculateSpectacularRefraction();
        });

        this.form.get('axis').valueChanges.subscribe(() =>{
            this.recalculateSpectecularRefractionCalcForm();
            if(!this.axisDisable) {
                this.axisCurrentValue = +this.form.get('axis').value;
            }
        });

        this.form.get('hsa').valueChanges.subscribe(() =>{
            this.recalculateSpectecularRefractionCalcForm();
            if(!this.hsaDisable) {
                this.hsaCurrentValue = +this.form.get('hsa').value;
            }
        });
    }

    private recalculateSpectacularRefraction() {
      if(this.form.get('sphere').value !== null && this.form.get('cylinder').value !== null) {
        let sphere = +this.i18nService.replaceFromCommaToDot(this.form.get('sphere').value);
        let cylinder = +this.i18nService.replaceFromCommaToDot(this.form.get('cylinder').value);

        this.sphereCylinderSpectecularRefractionForm(sphere, cylinder);
        this.sphereCylinderHSASpectecularRefractionForm(sphere, cylinder);
        this.sphereCylinderAxisSpectecularRefractionForm(sphere, cylinder);
        this.sphereCylinderAxisHSASpectecularRefractionForm(sphere, cylinder);

        this.recalculateSpectecularRefractionCalcForm();
      }
    }

    private sphereCylinderSpectecularRefractionForm(sphere: number, cylinder: number) {
        if((sphere >= -3 && sphere <= 3) && cylinder === 0) {
          if(this.hsaDisable === false) {
            this.hsaDisable = true;
            this.form.patchValue({'hsa': EMPTY_VALUE});
            this.form.controls['hsa'].disable();
          }
          if(this.axisDisable === false) {
            this.axisDisable = true;
            this.form.patchValue({'axis': EMPTY_VALUE});
            this.form.controls['axis'].disable();
          }
        }
      }
    
      private sphereCylinderHSASpectecularRefractionForm(sphere: number, cylinder: number) {
        if((sphere > 3 || sphere < -3) && cylinder === 0) {
          if(this.hsaDisable === true) {
            this.hsaDisable = false;
            this.form.patchValue({'hsa': this.hsaCurrentValue});
            this.form.controls['hsa'].enable();
          }
          if(this.axisDisable === false) {
            this.axisDisable = true;
            this.form.patchValue({'axis': EMPTY_VALUE});
            this.form.controls['axis'].disable();
          }
        }
      }
    
      private sphereCylinderAxisSpectecularRefractionForm(sphere: number, cylinder: number) {
        let spherePlusCylinder = sphere + cylinder;
        if((spherePlusCylinder >= -3 && spherePlusCylinder <= 3) && cylinder !== 0 && (sphere < 3 || sphere > -3)) {
          if(this.hsaDisable === false) {
            this.hsaDisable = true;
            this.form.patchValue({'hsa': EMPTY_VALUE});
            this.form.controls['hsa'].disable();
          }
          if(this.axisDisable === true) {
            this.axisDisable = false;
            this.form.patchValue({'axis': this.axisCurrentValue});
            this.form.controls['axis'].enable();
          }      
        }
      }
    
      private sphereCylinderAxisHSASpectecularRefractionForm(sphere: number, cylinder: number) {
        let spherePlusCylinder = sphere + cylinder;
        if( ((spherePlusCylinder > 3 || spherePlusCylinder < -3) && cylinder !== 0) || ((sphere > 3 || sphere < -3) && cylinder !== 0)) {
          if(this.axisDisable === true) {
            this.axisDisable = false;
            this.form.patchValue({'axis': this.axisCurrentValue});
            this.form.controls['axis'].enable();
          }
          if(this.hsaDisable === true) {
            this.hsaDisable = false;
            this.form.patchValue({'hsa': this.hsaCurrentValue});
            this.form.controls['hsa'].enable();
          }       
        }
      }

    private recalculateSpectecularRefractionCalcForm() {
      if(this.form.get('sphere').value !== null && this.form.get('cylinder').value !== null) {
      let sphere = +this.i18nService.replaceFromCommaToDot(this.form.get('sphere').value);
      let cylinder = +this.i18nService.replaceFromCommaToDot(this.form.get('cylinder').value);

        if(cylinder > 0) {
            let axis = +this.form.get('axis').value;
            let sphere2 = sphere + cylinder;
            let cylinder2 = cylinder * (-1);
            let axis2 = axis + 90;

            if(axis2 >= 180) {
              axis2 = axis2 - 180;
            }

            this.form.patchValue({'sphereCalc': this.i18nService.replaceFromDotToComma(sphere2)});
            this.form.patchValue({'cylinderCalc': this.i18nService.replaceFromDotToComma(cylinder2)});
            this.form.patchValue({'axisCalc': this.i18nService.replaceFromDotToComma(axis2)});
        } else {
          this.form.patchValue({'sphereCalc': this.i18nService.replaceFromDotToComma(sphere)});
          this.form.patchValue({'cylinderCalc': this.i18nService.replaceFromDotToComma(cylinder)});
        }

        if(+this.form.get('hsa').value > 0) {
            let x = +this.i18nService.replaceFromCommaToDot(this.form.get('sphereCalc').value);
            let y = +this.i18nService.replaceFromCommaToDot(this.form.get('sphereCalc').value) + +this.i18nService.replaceFromCommaToDot(this.form.get('cylinderCalc').value);

            if(Math.abs(x) >= 3) {
                x = x/(1 - +this.i18nService.replaceFromCommaToDot(this.form.get('hsa').value)*0.001*x);
            }
            if(Math.abs(y) >= 3) {
                y = y/(1 - +this.i18nService.replaceFromCommaToDot(this.form.get('hsa').value)*0.001*y);
            }
            this.form.patchValue({'sphereCalc': this.i18nService.replaceFromDotToComma(+x.toFixed(2))});
            this.form.patchValue({'cylinderCalc': this.i18nService.replaceFromDotToComma(+(y - x).toFixed(2))});
        }

        if(this.axisDisable) {
            this.form.patchValue({'axisCalc': EMPTY_VALUE});
        }

        if(this.hsaDisable) {
            this.form.patchValue({'hsaCalc': EMPTY_VALUE});
        } else {
            this.form.patchValue({'hsaCalc': 0});
        }
      }
    }

    checkRequiredFields() {

      if(this.form.get('sphere').value === "") {
        this.form.get('sphere').markAsTouched();
        this.form.get('sphere').setValue("");
      }

      if(this.form.get('cylinder').value === "") {
        this.form.get('cylinder').markAsTouched();
        this.form.get('cylinder').setValue("");
      }

      if(this.form.get('addition').value === "") {
        this.form.get('addition').markAsTouched();
        this.form.get('addition').setValue("");
      }

      if(!this.axisDisable && this.form.get('axis').value === "") {
        this.form.get('axis').markAsTouched();
        this.form.get('axis').setValue("");
      }

      if(!this.hsaDisable && this.form.get('hsa').value === "") {
        this.form.get('hsa').markAsTouched();
        this.form.get('hsa').setValue("");
      }
    }

    copy(side: LensSide) {
        this.copyEmit.emit(side);
    }

    switch(side: LensSide) {
        this.switchEmit.emit(side);
    }

    getLimitsFormatted(rangeMin: number, rangeMax: number) {
      return this.i18nService.getLimitsFormatted(rangeMin, rangeMax);
    }

    setValueWithFormat(featureName: string, control: string) {
      this.form.controls[control].setValue(
        this.i18nService.replaceFromDotToComma(
          this.i18nService.formatIfNumber(this.form.controls[control].value, this.globalStorageService.decimalFormats.find(feature => feature.featureName === featureName)?.decimalFormat)
        )
      );
    }

    clearAll() {
      this.form.reset();

      this.form.get('cylinder').setValue(0);
      this.form.get('addition').setValue(0);

      this.hsaCurrentValue = this.globalStorageService.hsa;
      
      this.axisDisable = true;
      this.hsaDisable = true;
  }
}

const EMPTY_VALUE: string = "-";